import React from 'react';
import ReactDOM from 'react-dom';
import { Trans, useTranslation } from 'react-i18next';
import { CloseIcon } from './CloseIcon';
import TagManager from 'react-gtm-module';
import './SharePromptModal.scss';

interface SharePromptModalProps {
  isOpen: boolean;
  onClose: () => void;
  onShare: () => void;
  actionType: 'buy' | 'sell';
  buildingAddress?: string;
  isSharing?: boolean;
  isMatch?: boolean;
}

const SharePromptModal: React.FC<SharePromptModalProps> = ({
  isOpen,
  onClose,
  onShare,
  actionType,
  buildingAddress,
  isSharing = false,
  isMatch = false
}) => {
  const { t } = useTranslation();

  if (!isOpen) return null;

  const handleShare = () => {
    TagManager.dataLayer({
      dataLayer: {
        event: `${actionType === 'buy' ? 'could_buy_shared_after_submit' : 'could_sell_shared_after_submit'}`,
        category: 'User Interaction',
        action: 'Click',
        label: 'Share',
        event_address: buildingAddress,
      },
    });
    onShare();
  };

  const handleSkip = () => {
    onClose();
  };

  return ReactDOM.createPortal(
    <div className="share-prompt-modal-overlay">
      <div className="share-prompt-modal">
        <button
          className="share-prompt-modal__close-button"
          onClick={handleSkip}
          disabled={isSharing}
          aria-label={t('common.close')}
        >
          <CloseIcon size={20} />
        </button>

        <div className="share-prompt-modal__content">
          <div className="share-prompt-modal__icon-wrapper">
            <div className="share-prompt-modal__icon">
              <svg 
                width="64" 
                height="64" 
                viewBox="0 0 24 24" 
                fill="none" 
                xmlns="http://www.w3.org/2000/svg"
              >
                <path 
                  d="M18 8.5C19.6569 8.5 21 7.15685 21 5.5C21 3.84315 19.6569 2.5 18 2.5C16.3431 2.5 15 3.84315 15 5.5C15 5.74019 15.0281 5.97391 15.0807 6.19719L8.91927 9.80281C8.41826 9.30563 7.74447 9 7 9C5.34315 9 4 10.3431 4 12C4 13.6569 5.34315 15 7 15C7.74447 15 8.41826 14.6944 8.91927 14.1972L15.0807 17.8028C15.0281 18.0261 15 18.2598 15 18.5C15 20.1569 16.3431 21.5 18 21.5C19.6569 21.5 21 20.1569 21 18.5C21 16.8431 19.6569 15.5 18 15.5C17.2555 15.5 16.5817 15.8056 16.0807 16.3028L9.91927 12.6972C9.97186 12.4739 10 12.2402 10 12C10 11.7598 9.97186 11.5261 9.91927 11.3028L16.0807 7.69719C16.5817 8.19437 17.2555 8.5 18 8.5Z" 
                  stroke="currentColor" 
                  strokeWidth="2" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                />
              </svg>
            </div>
            <div className="share-prompt-modal__celebration">
              <span className="share-prompt-modal__celebration-emoji">🎉</span>
            </div>
          </div>

          <h2 className="share-prompt-modal__title">
            <Trans i18nKey={isMatch ? `sharePrompt.${actionType}.matchTitle` : `sharePrompt.${actionType}.title`} />
          </h2>

          <p className="share-prompt-modal__description">
            <Trans 
              i18nKey={isMatch ? `sharePrompt.${actionType}.matchDescription` : `sharePrompt.${actionType}.description`}
              values={{ address: buildingAddress }}
            />
          </p>

          <div className="share-prompt-modal__benefits">
            <div className="share-prompt-modal__benefit">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z" fill="currentColor"/>
              </svg>
              <span><Trans i18nKey={`sharePrompt.benefit.${actionType === 'buy' ? 'findMatchesBuyer' : 'findMatchesSeller'}`} /></span>
            </div>
            <div className="share-prompt-modal__benefit">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17 20H22V18C22 16.3431 20.6569 15 19 15C18.0444 15 17.1931 15.4468 16.6438 16.1429M17 20H7M17 20V18C17 17.3438 16.8736 16.717 16.6438 16.1429M7 20H2V18C2 16.3431 3.34315 15 5 15C5.95561 15 6.80686 15.4468 7.35625 16.1429M7 20V18C7 17.3438 7.12642 16.717 7.35625 16.1429M7.35625 16.1429C7.88258 14.301 9.73893 13 12 13C14.2611 13 16.1174 14.301 16.6438 16.1429M15 7C15 9.20914 13.2091 11 11 11C8.79086 11 7 9.20914 7 7C7 4.79086 8.79086 3 11 3C13.2091 3 15 4.79086 15 7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span><Trans i18nKey="sharePrompt.benefit.reachMore" /></span>
            </div>
            <div className="share-prompt-modal__benefit">
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 8V12L15 15M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
              <span><Trans i18nKey="sharePrompt.benefit.faster" /></span>
            </div>
          </div>
        </div>

        <div className="share-prompt-modal__footer">
          <button
            className="share-prompt-modal__button share-prompt-modal__button--secondary"
            onClick={handleSkip}
            disabled={isSharing}
          >
            <Trans i18nKey="sharePrompt.skipButton" />
          </button>
          <button
            className="share-prompt-modal__button share-prompt-modal__button--primary"
            onClick={handleShare}
            disabled={isSharing}
          >
            {isSharing ? (
              <div className="share-prompt-modal__spinner"></div>
            ) : (
              <>
                <svg 
                  width="18" 
                  height="18" 
                  viewBox="0 0 24 24" 
                  fill="none" 
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path 
                    d="M8.59 13.51L15.42 17.49M15.41 6.51L8.59 10.49M21 5C21 6.65685 19.6569 8 18 8C16.3431 8 15 6.65685 15 5C15 3.34315 16.3431 2 18 2C19.6569 2 21 3.34315 21 5ZM9 12C9 13.6569 7.65685 15 6 15C4.34315 15 3 13.6569 3 12C3 10.3431 4.34315 9 6 9C7.65685 9 9 10.3431 9 12ZM21 19C21 20.6569 19.6569 22 18 22C16.3431 22 15 20.6569 15 19C15 17.3431 16.3431 16 18 16C19.6569 16 21 17.3431 21 19Z" 
                    stroke="currentColor" 
                    strokeWidth="2" 
                    strokeLinecap="round" 
                    strokeLinejoin="round"
                  />
                </svg>
                <Trans i18nKey="sharePrompt.shareButton" />
              </>
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default SharePromptModal;
